import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { useDeliveryCockpitTourContext } from '../../contexts/DeliveryCockpitTourContext';
import { useTourSpotlightRect } from '../../hooks/useTourSpotlightRect';

const TOUR_SPOTLIGHT_DESCRIPTION_ID = 'delivery-cockpit-tour-spotlight-description';
const OVERLAY_Z_INDEX = 1300;
const HIGHLIGHT_Z_INDEX = 1301;

/**
 * Dim page overlay with cutout around the active tour anchor.
 */
const TourSpotlight = () => {
    const {
        isTourOpen,
        isMinimized,
        currentStep,
    } = useDeliveryCockpitTourContext();

    const tourTarget = currentStep?.tourTarget;
    const enabled = Boolean(isTourOpen && !isMinimized && tourTarget);
    const { rect, targetElement } = useTourSpotlightRect(tourTarget, enabled);

    useEffect(() => {
        if (!enabled || !targetElement) {
            return undefined;
        }

        const previousDescribedBy = targetElement.getAttribute('aria-describedby');
        const describedByIds = new Set(
            (previousDescribedBy || '').split(/\s+/).filter(Boolean),
        );
        describedByIds.add(TOUR_SPOTLIGHT_DESCRIPTION_ID);
        targetElement.setAttribute('aria-describedby', Array.from(describedByIds).join(' '));
        targetElement.dataset.tourSpotlightActive = 'true';

        return () => {
            describedByIds.delete(TOUR_SPOTLIGHT_DESCRIPTION_ID);
            if (describedByIds.size > 0) {
                targetElement.setAttribute('aria-describedby', Array.from(describedByIds).join(' '));
            } else {
                targetElement.removeAttribute('aria-describedby');
            }
            delete targetElement.dataset.tourSpotlightActive;
        };
    }, [enabled, targetElement]);

    if (!enabled || !rect || typeof document === 'undefined') {
        return null;
    }

    const { top, left, width, height } = rect;
    const panelSx = {
        position: 'fixed',
        bgcolor: 'rgba(15, 23, 42, 0.62)',
        pointerEvents: 'auto',
        zIndex: OVERLAY_Z_INDEX,
    };

    return createPortal(
        <>
            <Box
                id={TOUR_SPOTLIGHT_DESCRIPTION_ID}
                sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    whiteSpace: 'nowrap',
                }}
            >
                {currentStep?.title}
                .
                {' '}
                {currentStep?.body}
            </Box>

            <Box
                data-testid="tour-spotlight-overlay"
                aria-hidden="true"
                sx={panelSx}
                style={{ top: 0, left: 0, right: 0, height: top }}
            />
            <Box
                aria-hidden="true"
                sx={panelSx}
                style={{ top, left: 0, width: left, height }}
            />
            <Box
                aria-hidden="true"
                sx={panelSx}
                style={{ top, left: left + width, right: 0, height }}
            />
            <Box
                aria-hidden="true"
                sx={panelSx}
                style={{ top: top + height, left: 0, right: 0, bottom: 0 }}
            />

            <Box
                data-testid="tour-spotlight-highlight"
                aria-hidden="true"
                sx={{
                    position: 'fixed',
                    top,
                    left,
                    width,
                    height,
                    borderRadius: 1,
                    boxShadow: '0 0 0 2px rgba(37, 99, 255, 0.95), 0 0 0 6px rgba(37, 99, 255, 0.25)',
                    pointerEvents: 'none',
                    zIndex: HIGHLIGHT_Z_INDEX,
                    transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
                }}
            />
        </>,
        document.body,
    );
};

export default TourSpotlight;
export { TOUR_SPOTLIGHT_DESCRIPTION_ID };
